import { api } from './api'

export interface RegistrationHistoryItem {
  id?: string
  attendeeId: string
  sessionId: string | number
  title: string
  subtitle?: string
  description?: string
  comments?: string
  date: string
  startDate?: string
  endDate?: string
  location?: string
  fee?: number
  schedule?: string
  campus?: string
  eventTypeId?: string
  confirmation?: string
  certificateReportId?: string
  email?: string
  onlineType?: string
  expirationDate?: string
  completedDate?: string
  creditType?: string
  creditHour?: string
  totalPoints?: number | string
  playerType?: string
  lmsTypeId?: number | null
  lms_type_id?: number | null
  LMSTypeID?: number | null
  paymentType?: string
  paymentStatus?: string
  payments?: UserRegistrationHistoryPayment[]
  onlineCategory?: string
  subscriptionLength?: string
  // Computed fields
  status?: string
  credits?: string
  isOnline?: boolean
  category?: string
  showCertificateLink?: boolean
  showEvaluationLink?: boolean
  hasEvaluation?: boolean
  evaluationCompleted?: boolean
  attended?: boolean
  evalId?: number
  canCancel?: boolean
  isCheckoutEnabled?: boolean
  // Resolved from the registrant's site/co-op policy override when one is configured, otherwise
  // the tenant default - see CancellationEligibilityResponse.PolicyTitle/PolicyText on the API.
  cancellationPolicyTitle?: string
  cancellationPolicyText?: string
}

export interface UserRegistrationHistoryPayment {
  paymentId: number
  attendeeId: number
  amount?: number | null
  type?: string | null
  status: string
  number?: string | null
  comments?: string | null
  transactionId?: number | null
  timeStamp?: string | null
  receiveDate?: string | null
  isRefund: boolean
  isCancellationFee: boolean
  cancellationFee?: number | null
}

export interface UpdateExpirationDateResponse {
  success: boolean
  attendeePk: number
  expirationDate: string
  message: string
}

export interface CancellationEligibility {
  canCancel: boolean
  reason?: string
  // Resolved cancellation policy text - a site/co-op override when one is configured for the
  // registrant's location site, otherwise the tenant default. Either may be absent if no policy
  // text is configured anywhere, in which case callers should fall back to static copy.
  policyTitle?: string
  policyText?: string
}

export interface CancellationRequest {
  reason?: string
}

export interface CancellationResponse {
  success: boolean
  message: string
}

export interface TransferRegistrationResponse {
  success: boolean
  originalAttendeeId: number
  originalAttendeeName: string
  originalAttendeeEmail: string
  substituteAttendeeId: number
  substituteName: string
  substituteEmail: string
  sessionId: number
}

export interface CheckOutRequest {
  attendee_id: string
  checkout_code: string
}

export interface CheckOutResponse {
  success: boolean
  message?: string
}

export interface PurchaseOrderAttachmentSummary {
  attachmentId: string
  poNumber?: string | null
  originalFileName?: string | null
  contentType?: string | null
  size?: number | null
  uploadedAtUtc?: string | null
}

export interface PurchaseOrderAttachmentStatus {
  success: boolean
  attendeeId: number
  sessionId?: number
  paidByPurchaseOrder: boolean
  canUpload: boolean
  hasAttachment: boolean
  attachment?: PurchaseOrderAttachmentSummary | null
}

export interface PurchaseOrderAttachmentUploadResponse {
  success: boolean
  message?: string
  attendeeId?: number
  sessionId?: number
  hasAttachment?: boolean
  attachment?: PurchaseOrderAttachmentSummary | null
}

export interface LegacyAttendeeRegistrationResponse {
  success?: boolean
  message?: string
  attendeePk?: number
  attendeeId?: number
  attendeeID?: number
  key?: number
  [key: string]: any
}

export interface RegistrationHistoryResponse {
  upcoming?: RegistrationHistoryItem[]
  history?: RegistrationHistoryItem[]
  online?: RegistrationHistoryItem[]
  canvas?: RegistrationHistoryItem[]
  past?: RegistrationHistoryItem[]
  registrations?: RegistrationHistoryItem[]
  totalCount?: number
  hasMore?: boolean
  [key: string]: any
}

const getLmsTypeId = (item: any): number | null => {
  const value =
    item?.lmsTypeId ??
    item?.lms_type_id ??
    item?.LMSTypeID ??
    item?.session?.lmsTypeId ??
    item?.session?.lms_type_id ??
    item?.session?.LMSTypeID ??
    item?.basic?.LMSTypeID
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const isCanvasRegistration = (item: any): boolean => getLmsTypeId(item) === 20040

const deduplicateRegistrations = (items: RegistrationHistoryItem[]): RegistrationHistoryItem[] => {
  const unique = new Map<string, RegistrationHistoryItem>()
  items.forEach((item, index) => {
    const key = `${item.attendeeId || item.id || index}-${item.sessionId}`
    unique.set(key, item)
  })
  return Array.from(unique.values())
}

// Helper function to transform API item to our expected format
const transformRegistrationItem = (item: any, category: string): RegistrationHistoryItem => {
  // Determine status based on category and onlineCategory
  let status = 'unknown'
  if (category === 'upcoming') {
    status = 'registered'
  } else if (category === 'past') {
    status = 'completed'
  } else if (item.onlineCategory) {
    switch (item.onlineCategory.toLowerCase().replace(/[\s_-]/g, '')) {
      case 'complete':
        status = 'completed'
        break
      case 'incomplete':
        status = 'incomplete'
        break
      case 'inprogress':
        status = 'in progress'
        break
      case 'awaitingevaluation':
        status = 'Awaiting Evaluation'
        break
      default:
        status = 'unknown'
    }
  }

  // Determine credits
  const credits = item.creditType || item.creditHour || 'Not specified'

  // Determine if it's online
  const isOnline = Boolean(
    category === 'online' ||
    item.onlineType ||
    item.onlineCategory ||
    item.title?.toLowerCase().includes('online') ||
    item.location?.toLowerCase().includes('online')
  )

  return {
    ...item,
    id: item.attendeeId || item.id,
    sessionId: item.sessionId,
    title: item.title,
    startDate: item.startDate || item.date,
    endDate: item.endDate,
    status,
    credits,
    location: item.location || item.campus,
    completionDate: item.completedDate,
    certificateUrl: item.certificateReportId ? `#certificate-${item.certificateReportId}` : undefined,
    isOnline,
    category,
    lmsTypeId: getLmsTypeId(item),
    isCheckoutEnabled: Boolean(
      item.isCheckoutEnabled ??
      item['is-checkout-enabled'] ??
      item['is_checkout_enabled']
    )
  }
}

// Helper function to categorize registrations
const categorizeRegistrations = (registrations: RegistrationHistoryItem[]): RegistrationHistoryResponse => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const upcoming: RegistrationHistoryItem[] = []
  const past: RegistrationHistoryItem[] = []
  const online: RegistrationHistoryItem[] = []
  const canvas: RegistrationHistoryItem[] = []
  
  registrations.forEach(registration => {
    const sessionDate = new Date(registration.startDate || registration.date)
    sessionDate.setHours(0, 0, 0, 0)
    
    // Canvas classification takes priority over date and online categories.
    if (isCanvasRegistration(registration)) {
      canvas.push(registration)
    }
    else if (registration.isOnline || registration.category === 'online' ||
        (registration.title && registration.title.toLowerCase().includes('online'))) {
      online.push(registration)
    }
    // Check if it's upcoming (future date)
    else if (sessionDate >= today) {
      upcoming.push(registration)
    }
    // Otherwise it's past
    else {
      past.push(registration)
    }
  })
  
  return {
    upcoming,
    past,
    online,
    canvas,
    totalCount: registrations.length,
    hasMore: false
  }
}

/** Session IDs (as strings) the user holds an active registration for, across every history bucket. */
export function getEnrolledSessionIds(history: RegistrationHistoryResponse): Set<string> {
  const rawOnline = history.online as unknown
  const onlineItems: RegistrationHistoryItem[] = Array.isArray(rawOnline)
    ? rawOnline
    : rawOnline && typeof rawOnline === 'object'
      ? Object.values(rawOnline as Record<string, unknown>).flatMap(items =>
        Array.isArray(items) ? items as RegistrationHistoryItem[] : [],
      )
      : []

  const ids = new Set<string>()
  for (const item of [
    ...(history.upcoming || []),
    ...(history.past || []),
    ...(history.history || []),
    ...(history.registrations || []),
    ...(history.canvas || []),
    ...onlineItems,
  ]) {
    if (item?.sessionId != null && String(item.sessionId) !== '') ids.add(String(item.sessionId))
  }
  return ids
}

let forcedHistoryRequest: Promise<RegistrationHistoryResponse> | null = null

export const registrationService = {
  /** Bypasses the Mongo cache so the result reflects the authoritative database. */
  getEnrolledSessionIds: async (): Promise<Set<string>> => {
    return getEnrolledSessionIds(await registrationService.getRegistrationHistory(true))
  },

  isRegisteredForSession: async (sessionId: number, registrantEmail: string): Promise<boolean> => {
    const result = await api.get<{ isRegistered: boolean }>('/user/group-registration-status', {
      params: { sessionId, registrantEmail },
    })
    if (typeof result.isRegistered !== 'boolean') throw new Error('Unable to verify registration status.')
    return result.isRegistered
  },
  registerEventSessionAttendee: async (params: {
    userId: number
    sessionId: number
  }): Promise<LegacyAttendeeRegistrationResponse> => {
    return api.post<LegacyAttendeeRegistrationResponse>('/legacydb/eventsessionattendees/register', {
      userId: params.userId,
      sessionId: params.sessionId,
    })
  },

  // Get user registration history
  getRegistrationHistory: (force = false): Promise<RegistrationHistoryResponse> => {
    if (!force) return registrationService.fetchRegistrationHistory(false)
    // force=true makes the API delete and re-insert the user's cached history, so overlapping
    // forced reloads can leave duplicate rows. Callers share the request that is already running.
    forcedHistoryRequest ??= registrationService.fetchRegistrationHistory(true)
      .finally(() => { forcedHistoryRequest = null })
    return forcedHistoryRequest
  },

  fetchRegistrationHistory: async (force: boolean): Promise<RegistrationHistoryResponse> => {
    try {
      // Increase timeout to 60 seconds for this slow endpoint
      const response = await api.get(`/user/registration-history?force=${force}`, { timeout: 60000 })
      
      // If response already has categorized data, transform it to our expected format
      if (response.upcoming || response.history || response.online || response.canvas) {
        const upcomingItems = (response.upcoming || []).map((item: any) => transformRegistrationItem(item, 'upcoming'))
        const pastItems = (response.history || []).map((item: any) => transformRegistrationItem(item, 'past'))
        const onlineItems = Array.isArray(response.online)
          ? response.online.map((item: any) => transformRegistrationItem(item, 'online'))
          : response.online && typeof response.online === 'object'
            ? Object.entries(response.online as Record<string, unknown>).flatMap(([bucket, items]) =>
              Array.isArray(items)
                ? items.map((item: any) => transformRegistrationItem({
                    ...item,
                    onlineCategory: item.onlineCategory || bucket,
                  }, 'online'))
                : []
            )
            : []
        const canvasBucketItems = Array.isArray(response.canvas)
          ? response.canvas.map((item: any) => transformRegistrationItem(item, 'online'))
          : []
        const canvasItems = deduplicateRegistrations(
          [...canvasBucketItems, ...upcomingItems, ...pastItems, ...onlineItems].filter(isCanvasRegistration)
        )

        // Transform the response to match our expected format
        const transformedResponse: RegistrationHistoryResponse = {
          upcoming: upcomingItems.filter((item: RegistrationHistoryItem) => !isCanvasRegistration(item)),
          past: pastItems.filter((item: RegistrationHistoryItem) => !isCanvasRegistration(item)),
          online: onlineItems.filter((item: RegistrationHistoryItem) => !isCanvasRegistration(item)),
          canvas: canvasItems,
          totalCount: canvasBucketItems.length + upcomingItems.length + pastItems.length + onlineItems.length,
          hasMore: false
        }
        
        return transformedResponse
      }
      
      // If response has a single registrations array, categorize it
      if (response.registrations && Array.isArray(response.registrations)) {
        const categorized = categorizeRegistrations(response.registrations)
        return categorized
      }
      
      // If response is an array directly
      if (Array.isArray(response)) {
        const categorized = categorizeRegistrations(response)
        return categorized
      }
      
      return response
    } catch (error: any) {
      // No registration history found - treat as an empty result rather than an error
      if (error?.response?.status === 404) {
        return { upcoming: [], past: [], online: [], canvas: [], totalCount: 0, hasMore: false }
      }
      throw error
    }
  },

  // Get registration history with fallback
  getRegistrationHistoryWithFallback: async (): Promise<RegistrationHistoryResponse> => {
    try {
      // First try with force=false
      const response = await registrationService.getRegistrationHistory(false)
      
      // If no data, try with force=true
      if ((!response.upcoming || response.upcoming.length === 0) && 
          (!response.past || response.past.length === 0) && 
          (!response.online || response.online.length === 0)) {
        return await registrationService.getRegistrationHistory(true)
      }
      
      return response
    } catch (error) {
      throw error
    }
  },

  // Check if a registration can be cancelled
  checkCancellationEligibility: async (attendeeId: string): Promise<CancellationEligibility> => {
    try {
      const response = await api.get(`/user/registrations/${attendeeId}/cancellation-eligibility`)
      return response
    } catch (error) {
      throw error
    }
  },

  // Cancel a registration
  cancelRegistration: async (attendeeId: string, reason?: string): Promise<CancellationResponse> => {
    try {
      const payload: CancellationRequest = reason ? { reason } : {}
      const response = await api.post(`/user/registrations/${attendeeId}/cancel`, payload)
      return response
    } catch (error) {
      throw error
    }
  },

  // The registration confirmation email for one of the caller's registrations, rendered by the server as HTML.
  getRegistrationConfirmationHtml: async (attendeeId: string): Promise<string> => {
    return api.get<string>(`/user/registrations/${attendeeId}/confirmation`, { responseType: 'text' })
  },

  // Hand the caller's own upcoming registration to another person (identified by their account
  // email) in the same session. The server enforces ownership, timing, and eligibility.
  transferRegistration: async (params: {
    attendeeId: string
    substituteEmail: string
    reason?: string
  }): Promise<TransferRegistrationResponse> => {
    const attendeeId = Number(params.attendeeId)
    try {
      const response = await api.post<TransferRegistrationResponse>(
        '/legacydb/eventsessionattendees/substitute/self',
        {
          attendeeId,
          substituteEmail: params.substituteEmail.trim(),
          reason: params.reason?.trim() || undefined,
        },
        // The API commits the swap and then sends every notification email before it responds.
        { skipRetry: true, timeout: 120000 },
      )
      if (!response?.success) throw new Error('')
      return response
    } catch (error: any) {
      // The swap may have committed even though this response was lost or failed (a timeout, a
      // gateway error, or a retry of a transfer that already went through). The substitution link
      // is written in the same transaction, so its presence means the transfer succeeded.
      const committed = await registrationService.findTransfer(attendeeId).catch(() => null)
      if (committed) return committed
      // An empty message tells the caller to show its own localized fallback.
      throw new Error(error?.response?.data?.error || error?.response?.data?.message || '')
    }
  },

  findTransfer: async (originalAttendeeId: number): Promise<TransferRegistrationResponse | null> => {
    const response = await api.get<{ substitutions?: Omit<TransferRegistrationResponse, 'success'>[] }>(
      `/legacydb/eventsessionattendees/substitutions?attendeeIds=${originalAttendeeId}`,
      { skipRetry: true },
    )
    const link = response?.substitutions?.find(s => Number(s.originalAttendeeId) === originalAttendeeId)
    return link ? { ...link, success: true } : null
  },

  checkOut: async (params: {
    attendeeId: string
    code: string
  }): Promise<CheckOutResponse> => {
    const attendeeId = (params.attendeeId || '').trim()
    const code = (params.code || '').trim()

    if (!attendeeId) {
      return { success: false, message: 'Missing attendee_id for check-out' }
    }
    if (!code) {
      return { success: false, message: 'Missing checkout code' }
    }

    const payload: CheckOutRequest = {
      attendee_id: attendeeId,
      checkout_code: code,
    }

    try {
      const response = await api.post<CheckOutResponse>('/user/attendee-status/check-out', payload)
      return {
        success: response?.success ?? true,
        message: response?.message,
      }
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to submit check-out code'
      return { success: false, message: apiMessage }
    }
  },

  uploadPurchaseOrderAttachment: async (
    attendeeId: string,
    params: { file: File; poNumber?: string },
  ): Promise<PurchaseOrderAttachmentUploadResponse> => {
    const formData = new FormData()
    formData.append('file', params.file)
    const poNumber = params.poNumber?.trim()
    if (poNumber) formData.append('poNumber', poNumber)

    return api.post<PurchaseOrderAttachmentUploadResponse>(`/user/registrations/${attendeeId}/po-attachment`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      skipRetry: true,
    })
  },

  getPurchaseOrderAttachmentStatus: async (attendeeId: string): Promise<PurchaseOrderAttachmentStatus> => {
    return api.get<PurchaseOrderAttachmentStatus>(`/user/registrations/${attendeeId}/po-attachment`, {
      skipAuthRedirect: true,
    })
  },

  // Update expiration date for an online course registration
  updateExpirationDate: async (attendeePk: string, subscriptionLengthDays: number): Promise<UpdateExpirationDateResponse> => {
    try {
      // Calculate expiration date based on subscription length
      const expirationDate = new Date()
      expirationDate.setDate(expirationDate.getDate() + subscriptionLengthDays)
      
      const payload = {
        attendeePk: parseInt(attendeePk),
        expirationDate: expirationDate.toISOString()
      }
      
      const response = await api.put<UpdateExpirationDateResponse>('/user/update-expiration-date', payload)
      
      return response
    } catch (error: any) {
      throw error
    }
  }
}
